import { KeepAwake } from '@capacitor-community/keep-awake';
import { App as CapacitorApp } from '@capacitor/app';
import { Capacitor, type PluginListenerHandle } from '@capacitor/core';
import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { gameStore } from '../socket/game-store';
import { useGameStore } from '../socket/hooks';
import { useI18n } from './i18n';

const ACTIVE_ROUND_PHASES = new Set([
  'ROUND_COUNTDOWN',
  'WISH_INPUT',
  'CONSEQUENCE_INPUT',
  'VOTE_PREVIEW',
  'VOTE_OPEN',
  'REVEAL',
]);

function joinPathFromUrl(rawUrl: string): string | null {
  try {
    const url = new URL(rawUrl);
    const candidate = `${url.hostname === 'join' ? '/join' : ''}${url.pathname}`;
    const match = /^\/join\/([a-z]{5})\/?$/i.exec(candidate);
    return match?.[1] === undefined ? null : `/join/${match[1].toUpperCase()}`;
  } catch {
    return null;
  }
}

export function NativePlatformEffects() {
  const state = useGameStore();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { text } = useI18n();
  const isNative = Capacitor.isNativePlatform();
  const liveRound = state.snapshot !== null && ACTIVE_ROUND_PHASES.has(state.snapshot.phase);

  useEffect(() => {
    if (!isNative) return;
    document.documentElement.dataset.platform = Capacitor.getPlatform();
    const listeners: PluginListenerHandle[] = [];
    let disposed = false;

    const addListeners = async () => {
      const appState = await CapacitorApp.addListener('appStateChange', ({ isActive }) => {
        if (isActive) gameStore.connect();
      });
      const deepLink = await CapacitorApp.addListener('appUrlOpen', ({ url }) => {
        const path = joinPathFromUrl(url);
        if (path !== null) navigate(path);
      });
      const launch = await CapacitorApp.getLaunchUrl();
      const launchPath = launch === undefined ? null : joinPathFromUrl(launch.url);
      if (launchPath !== null) navigate(launchPath);
      const back = await CapacitorApp.addListener('backButton', ({ canGoBack }) => {
        if (pathname.startsWith('/room/') && liveRound && !window.confirm(text('native.leaveRoundConfirm'))) return;
        if (canGoBack) navigate(-1);
        else void CapacitorApp.exitApp();
      });
      if (disposed) {
        await Promise.all([appState.remove(), deepLink.remove(), back.remove()]);
      } else {
        listeners.push(appState, deepLink, back);
      }
    };
    void addListeners();
    return () => {
      disposed = true;
      for (const listener of listeners) void listener.remove();
      delete document.documentElement.dataset.platform;
    };
  }, [isNative, liveRound, navigate, pathname, text]);

  useEffect(() => {
    if (!isNative) return;
    if (liveRound) void KeepAwake.keepAwake();
    else void KeepAwake.allowSleep();
    return () => { void KeepAwake.allowSleep(); };
  }, [isNative, liveRound]);

  return null;
}

export { joinPathFromUrl };
